// frontend/src/components/Product/ProductReactions.jsx - CLEANED
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { addReaction, getUserReaction, getReactionCounts } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbsUp,
  faThumbsDown,
  faHeart,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const REACTIONS = [
  {
    type: "like",
    icon: faThumbsUp,
    label: "Like",
    color: "#3b82f6",
    bgColor: "#dbeafe",
  },
  {
    type: "dislike",
    icon: faThumbsDown,
    label: "Dislike",
    color: "#ef4444",
    bgColor: "#fee2e2",
  },
  {
    type: "love",
    icon: faHeart,
    label: "Love",
    color: "#ec4899",
    bgColor: "#fce7f3",
  },
  {
    type: "recommend",
    icon: faCheckCircle,
    label: "Recommend",
    color: "#10b981",
    bgColor: "#d1fae5",
  },
  {
    type: "dont_recommend",
    icon: faTimesCircle,
    label: "Don't Recommend",
    color: "#f59e0b",
    bgColor: "#fef3c7",
  },
];

const ProductReactions = ({ productId, onReactionChange }) => {
  const { user } = useAuth();
  const [reactionCounts, setReactionCounts] = useState({
    like: 0,
    dislike: 0,
    love: 0,
    recommend: 0,
    dont_recommend: 0,
  });
  const [userReaction, setUserReaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalReactions, setTotalReactions] = useState(0);

  useEffect(() => {
    fetchReactionData();
  }, [productId]);

  const fetchReactionData = async () => {
    try {
      const [countsRes, userReactionRes] = await Promise.all([
        getReactionCounts(productId),
        user
          ? getUserReaction(productId)
          : Promise.resolve({ data: { reaction: null } }),
      ]);
      setReactionCounts(countsRes.data.counts);
      setUserReaction(userReactionRes.data.reaction);
      const total = Object.values(countsRes.data.counts).reduce(
        (sum, val) => sum + val,
        0,
      );
      setTotalReactions(total);
    } catch (error) {
      console.error("Error fetching reaction data:", error);
    }
  };

  const handleReaction = async (reactionType) => {
    if (!user) {
      alert("Please login to react to products");
      return;
    }

    setLoading(true);
    try {
      const { data } = await addReaction(productId, reactionType);
      setReactionCounts(data.counts);
      setUserReaction(data.action === "removed" ? null : reactionType);
      const total = Object.values(data.counts).reduce(
        (sum, val) => sum + val,
        0,
      );
      setTotalReactions(total);
      if (onReactionChange) onReactionChange(data.counts);
    } catch (error) {
      console.error("Error adding reaction:", error);
      alert(error.response?.data?.message || "Failed to add reaction");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="product-reactions"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="reactions-header">
        <h4>What do you think about this product?</h4>
        {totalReactions > 0 && (
          <span className="total-reactions">{totalReactions} reactions</span>
        )}
      </div>

      <div className="reactions-grid">
        {REACTIONS.map((reaction) => {
          const isActive = userReaction === reaction.type;
          const count = reactionCounts[reaction.type] || 0;

          return (
            <motion.button
              key={reaction.type}
              className={`reaction-btn ${isActive ? "active" : ""}`}
              onClick={() => handleReaction(reaction.type)}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                borderColor: isActive ? reaction.color : "#e5e5e5",
                background: isActive ? reaction.bgColor : "transparent",
              }}
            >
              <FontAwesomeIcon
                icon={reaction.icon}
                style={{ color: isActive ? reaction.color : "#5A5A5A" }}
              />
              <span className="reaction-label">{reaction.label}</span>
              {count > 0 && <span className="reaction-count">{count}</span>}
              {loading && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="loading-spinner"
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default ProductReactions;
